import * as React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from './button.js';
import { cn } from '../lib/utils.js';

// ─── Types ────────────────────────────────────────────────────

interface ModalDialogProps {
  /** Content rendered in the header (title + optional subtitle) */
  header: React.ReactNode;
  /** Dialog body content */
  children: React.ReactNode;
  /** Footer content — typically action buttons. Omit to hide the footer bar entirely. */
  footer?: React.ReactNode;
  onClose: () => void;
  /** Max width class — defaults to max-w-md */
  maxWidth?: string;
  /** z-index class — defaults to z-50 */
  zIndex?: string;
  /** aria-label for the close button */
  closeLabel?: string;
  className?: string;
  /** data-testid forwarded to the dialog wrapper for E2E selectors */
  testId?: string;
}

// Stacked dialogs (a child dialog opened from a parent's footer) must not all close on one Escape.
const openDialogs: symbol[] = [];

// ─── Component ───────────────────────────────────────────────

function ModalDialog({
  header,
  children,
  footer,
  onClose,
  maxWidth = 'max-w-md',
  zIndex = 'z-50',
  closeLabel = 'Zamknij',
  className,
  testId,
}: ModalDialogProps) {
  const titleId = React.useId();
  const dialogKey = React.useRef(Symbol('modal-dialog'));
  React.useEffect(() => {
    const key = dialogKey.current;
    openDialogs.push(key);
    return () => {
      openDialogs.splice(openDialogs.indexOf(key), 1);
    };
  }, []);

  // Registered in a layout effect so it precedes the passive-effect listeners of Radix overlays that
  // mount with or after this dialog; both listen on `document` in capture phase, in registration order.
  const escapeClaimedBeforeDialogOpened = React.useRef(false);
  React.useLayoutEffect(() => {
    const record = (event: KeyboardEvent) => {
      escapeClaimedBeforeDialogOpened.current = event.defaultPrevented;
    };

    document.addEventListener('keydown', record, true);

    return () => {
      document.removeEventListener('keydown', record, true);
    };
  }, []);

  React.useEffect(() => {
    const key = dialogKey.current;

    // A Radix overlay opened inside the dialog preventDefaults the Escape it consumes, so that Escape
    // must not also close the dialog. An overlay that was already open underneath (e.g. the popover a
    // picker dialog was launched from) preventDefaults it too, but did so before this dialog's
    // capture listener ran and must not swallow the dialog's own Escape.
    const handleKeyDown = (event: KeyboardEvent) => {
      const consumedByInnerOverlay =
        event.defaultPrevented && !escapeClaimedBeforeDialogOpened.current;
      if (event.key === 'Escape' && !consumedByInnerOverlay && openDialogs.at(-1) === key) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const content = (
    <div className={cn('fixed inset-0 flex items-center justify-center', zIndex)}>
      <div aria-hidden="true" className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={testId}
        className={cn(
          'relative z-10 bg-background rounded-xl shadow-xl border border-border w-full mx-4 flex flex-col max-h-[90vh]',
          maxWidth,
          className,
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-border/50 shrink-0">
          <div id={titleId} className="flex-1 min-w-0">
            {header}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label={closeLabel}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {/* Footer */}
        {footer != null ? (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border/50 shrink-0">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return content;
  }

  return createPortal(content, document.body);
}

export { ModalDialog };
export type { ModalDialogProps };
